import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { 
  ShoppingCart, 
  Package, 
  Warehouse, 
  TrendingUp, 
  Headphones, 
  BarChart3,
  Target,
  Shield
} from 'lucide-react';

const WhatWeDo = () => {
  const services = [
    {
      icon: <ShoppingCart className="w-12 h-12" />,
      title: 'Full-Service Amazon Account Management',
      description: 'We handle every aspect of your Amazon presence—from account setup and optimization to daily operations and performance monitoring. Our team ensures your brand maintains a professional, competitive presence on the platform.',
      features: ['Account Setup & Optimization', 'Daily Operations Management', 'Performance Monitoring', 'Brand Protection']
    },
    {
      icon: <Package className="w-12 h-12" />,
      title: 'Wholesale Purchasing Model',
      description: 'We purchase your products upfront, eliminating your financial risk and cash flow concerns. This allows you to focus on manufacturing while we handle the complexities of e-commerce sales.',
      features: ['Upfront Product Purchase', 'No Financial Risk', 'Guaranteed Payments', 'Flexible Terms']
    },
    {
      icon: <Warehouse className="w-12 h-12" />,
      title: 'Logistics + FBA Preparation',
      description: 'Our nationwide warehouse network receives, inspects, labels, and prepares your inventory for Amazon FBA. We ensure compliance with all Amazon requirements and optimize for Prime eligibility.',
      features: ['Nationwide Warehouses', 'Quality Inspection', 'FBA Preparation', 'Prime Optimization']
    },
    {
      icon: <TrendingUp className="w-12 h-12" />,
      title: 'AI-Powered Advertising',
      description: 'Our proprietary AI-driven PPC platform manages over $75K in monthly ad spend, continuously optimizing bids and targeting for maximum ROI. We leverage machine learning to stay ahead of market trends.',
      features: ['AI-Driven Campaigns', 'Real-Time Optimization', 'ROI Maximization', 'Market Trend Analysis']
    },
    {
      icon: <BarChart3 className="w-12 h-12" />,
      title: 'Growth Analytics',
      description: 'Gain deep insights into your sales performance with our comprehensive analytics dashboard. We provide detailed reporting on revenue, inventory turnover, customer behavior, and competitive positioning.',
      features: ['Sales Analytics', 'Inventory Reports', 'Customer Insights', 'Competitive Analysis']
    },
    {
      icon: <Headphones className="w-12 h-12" />,
      title: 'Dedicated Customer Support',
      description: 'Our customer service team handles all inquiries, reviews, and issues on your behalf, maintaining your brand reputation and ensuring customer satisfaction around the clock.',
      features: ['24/7 Support', 'Review Management', 'Issue Resolution', 'Brand Reputation']
    },
    {
      icon: <Target className="w-12 h-12" />,
      title: 'Listing Optimization',
      description: 'We create and optimize product listings with SEO-driven titles, compelling descriptions, and high-quality images to maximize visibility and conversion rates.',
      features: ['SEO Optimization', 'Compelling Copywriting', 'Image Enhancement', 'A/B Testing']
    },
    {
      icon: <Shield className="w-12 h-12" />,
      title: 'Brand Protection',
      description: 'We actively monitor and protect your brand from unauthorized sellers, counterfeit products, and pricing violations, ensuring your brand integrity across all marketplaces.',
      features: ['Unauthorized Seller Monitoring', 'Counterfeit Detection', 'MAP Enforcement', 'Brand Registry Management']
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section 
        className="relative py-32 bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1666875753105-c63a6f3bdc86?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwxfHxkYXRhJTIwdmlzdWFsaXphdGlvbnxlbnwwfHx8fDE3NzIyNzA2OTN8MA&ixlib=rb-4.1.0&q=85)'
        }}
      >
        <div className="absolute inset-0 bg-slate-900/90"></div>
        <div className="container mx-auto px-6 relative z-10">
          <h1 className="text-5xl md:text-6xl font-bold text-white text-center mb-6">
            What We <span className="text-[#FF9900]">Do</span>
          </h1>
          <p className="text-xl text-gray-200 text-center max-w-3xl mx-auto">
            Comprehensive e-commerce solutions that drive measurable results
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {services.map((service, index) => (
                <Card 
                  key={index} 
                  className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                >
                  <CardContent className="p-8">
                    <div className="text-[#FF9900] mb-4">
                      {service.icon}
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-4">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed mb-6">
                      {service.description}
                    </p>
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center text-gray-700">
                          <div className="w-2 h-2 bg-[#FF9900] rounded-full mr-3"></div>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-900">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to <span className="text-[#FF9900]">Elevate</span> Your Amazon Presence?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
            Partner with EYME LLC and experience the difference that strategic e-commerce management can make.
          </p>
          <a href="/contact">
            <button className="bg-[#FF9900] hover:bg-[#e68a00] text-white px-10 py-4 text-lg font-semibold rounded-lg shadow-lg transform transition-all duration-300 hover:scale-105">
              Get Started Today
            </button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default WhatWeDo;
